import { useState, useContext, useEffect, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import Loading from 'react-loading';
import './style.css'
import './styleResponsive.css'
import './kanban-redesign.css'
import '../../styles/page-titles.css'
import styled from 'styled-components';
import { IoReceiptOutline } from "react-icons/io5";
import { getStoredUser } from '../../utils/safeStorage';
import { toast } from "react-toastify";

import { getPedido } from "../../services/api";
import PermissionGate from "../../services/PermissionGate";
import { useNavigate } from "react-router-dom";
import { SidebarContext } from "../../AppRoutes";
import { useEmpresa } from "../../contexts/empresaContext";

import * as AiIcons from 'react-icons/ai';
import { FaSearch } from "react-icons/fa";

// Hooks customizados
import usePedidos from "../../hooks/usePedidos";
import useWebSocket from "../../hooks/useWebSocket";

// Componentes
import ModalEditTempoEntrega from "../../components/ModalEditTempoEntrega";
import ModalPedido from "../../components/ModalPedido";
import ModalAddEntregador from "./ModalAddEntregador";
import { MobileKanban } from "./MobileKanban";
import KanbanColumn from "../../components/KanbanColumn";
import FilterButtons from "../../components/FilterButtons";


const Teste = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
    min-height: 100vh;
    width: auto;
    flex: 1;
    transition: margin-left 200ms ease;
    background-color: #ffffff;
    overflow: visible;
    z-Index: 9;

    @media (max-width: 880px) {
        margin-left: 0;
    }
`;

const KanbanGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    width: 100%;
    max-width: 100%;
    min-height: 400px;
    box-sizing: border-box;

    /* Telas WQHD/FullHD: gap padrão */
    @media (max-width: 1600px) {
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 8px;
    }

    /* Telas HD (1366) e similares: gap mínimo para ganhar largura nas colunas */
    @media (max-width: 1500px) {
        gap: 6px;
    }

    @media (max-width: 1100px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 10px;
    }

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }

    @media (max-height: 800px) {
        min-height: 300px;
    }
`;

const Ordercolumn = styled.div`
    min-width: 0;
    width: 100%;
`;

const User = () => {
    // Hook de tradução
    const { t } = useTranslation(['orders', 'common']);

    // Estados de autenticação e empresa
    const empresa = localStorage.getItem('empresa');
    const empresaParse = JSON.parse(empresa);
    const objIdEmpresa = empresaParse._id;
    const idEmpresa = empresaParse.id_empresa;
    const vinculo_empresa = empresaParse.cnpj;

    const userParse = getStoredUser();
    const userID = userParse?._id;

    // Estados da UI
    const { sidebar } = useContext(SidebarContext);
    const { refreshEmpresaConfig } = useEmpresa();
    const navigate = useNavigate();
    const [filtroSelecionado, setFiltroSelecionado] = useState("all");
    const [searchQuery, setSearchQuery] = useState('');
    const [filterIsLoading, setFilterIsLoading] = useState(false);

    // Estados dos modais
    const [showEditTempoEntregaEmpresa, setEditTempoEntregaEmpresa] = useState(true);
    const [showPedido, setShowPedido] = useState(true);
    const [showModalAddEntregador, setShowModalAddEntregador] = useState(true);
    const [infoPedido, setInfoPedido] = useState({});
    const [pedidoToAddEntregador, setPedidoToAddEntregador] = useState({});

    // Hook customizado para gerenciar pedidos
    const {
        pedidosOrganizados,
        empresaConfig,
        isLoading,
        isUpdating,
        pedidosProcessando,  // ✅ NOVO: Para feedback visual instantâneo
        loadPedidos,
        avancarPedido,
        updateFromWebSocket,
        filtrarPedidos,
        buscarPedidos
    } = usePedidos(userID, idEmpresa, vinculo_empresa);

    // Configuração da API URL
    const isDevelopment = window.location.hostname === 'localhost';
    const apiUrl = isDevelopment
        ? process.env.REACT_APP_SERVER_URL_DEV
        : process.env.REACT_APP_SERVER_URL_PROD;

    // Hook customizado para WebSocket
    useWebSocket(apiUrl, objIdEmpresa, userID, updateFromWebSocket);

    // Hook para detectar mobile
    const useIsMobile = () => {
        const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

        useEffect(() => {
            const handleResize = () => {
                setIsMobile(window.innerWidth <= 1200);
            };

            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }, []);

        return isMobile;
    };

    const isMobile = useIsMobile();

    // Carregar pedidos na inicialização
    useEffect(() => {
        loadPedidos();
    }, [loadPedidos]);

    // Handlers otimizados com useCallback
    const handleCadastroPDV = useCallback(() => {
        navigate('/pdv');
    }, [navigate]);

    const handleAddEntregador = useCallback((event, pedido) => {
        event.stopPropagation();
        setPedidoToAddEntregador(pedido);
        setShowModalAddEntregador(!showModalAddEntregador);
    }, [showModalAddEntregador]);

    const handleAvancar = useCallback(async (event, _id, id_pedido, status_pedido) => {
        event.stopPropagation();
        await avancarPedido(_id, id_pedido, status_pedido);
    }, [avancarPedido]);

    const handleEditTempoEntrega = useCallback(() => {
        setEditTempoEntregaEmpresa(!showEditTempoEntregaEmpresa);
    }, [showEditTempoEntregaEmpresa]);

    // Callback para refresh após editar configurações da empresa (força bypass do cache)
    const handleRefreshAfterConfigEdit = useCallback(async () => {
        await refreshEmpresaConfig(); // Força refresh da config (bypass cache de 5 min)
        await loadPedidos(); // Recarrega pedidos
    }, [refreshEmpresaConfig, loadPedidos]);

    // Refresh que mantém o filtro ativo (usado ao atualizar pagamento, etc.)
    const handleRefreshKeepFilter = useCallback(async () => {
        await loadPedidos();
        // Reaplicar filtro ativo após recarregar os dados
        if (filtroSelecionado !== "all") {
            // Aguardar próximo tick para garantir que originalPedidos foi atualizado
            setTimeout(() => filtrarPedidos(filtroSelecionado), 0);
        }
    }, [loadPedidos, filtrarPedidos, filtroSelecionado]);

    // Handlers de filtro otimizados
    const handleFilterAll = useCallback(async () => {
        setFilterIsLoading(true);
        setFiltroSelecionado("all");
        filtrarPedidos("all");
        setFilterIsLoading(false);
    }, [filtrarPedidos]);

    const handleFilterDelivery = useCallback(async () => {
        setFilterIsLoading(true);
        setFiltroSelecionado("delivery");
        filtrarPedidos("delivery");
        setFilterIsLoading(false);
    }, [filtrarPedidos]);

    const handleFilterBalcao = useCallback(async () => {
        setFilterIsLoading(true);
        setFiltroSelecionado("balcao");
        filtrarPedidos("balcao");
        setFilterIsLoading(false);
    }, [filtrarPedidos]);

    const handleFilterMesas = useCallback(async () => {
        setFilterIsLoading(true);
        setFiltroSelecionado("mesas");
        filtrarPedidos("mesas");
        setFilterIsLoading(false);
    }, [filtrarPedidos]);

    const handleShowPedido = useCallback(async (idPedidoToOpen, id_empresa) => {
        setShowPedido(!showPedido);
        try {
            const response = await getPedido(userID, id_empresa, vinculo_empresa, idPedidoToOpen);
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log("response-getPedido:", response);
            if (showPedido && response.data.pedido) {
                setInfoPedido(response.data.pedido);
            }
        } catch (error) {
            toast(t('common:messages.error'), { type: 'error' });
        }
    }, [showPedido, userID, vinculo_empresa]);



    // Componente para tempo de entrega na coluna de análise
    const AnaliseHeaderContent = () => {
        const autoAcceptLabel = empresaConfig.tipoImpressao === 'manual' ? t('orders:config.disabled') :
                                empresaConfig.tipoImpressao === 'automatico' ? t('orders:config.enabled') : t('orders:config.notDefined');
        const autoAcceptStatus = empresaConfig.tipoImpressao === 'automatico' ? 'enabled' :
                                  empresaConfig.tipoImpressao === 'manual' ? 'disabled' : '';

        return (
            <div className="store-config">
                <div className="store-config-items">
                    <div className="store-config-row">
                        <span className="store-config-label">🏪 {t('orders:config.counterTime')}:</span>
                        <span className="store-config-badge">{empresaConfig.tempoBalcaoMin} a {empresaConfig.tempoBalcaoMax} min</span>
                    </div>
                    <div className="store-config-row">
                        <span className="store-config-label">🛵 {t('orders:config.deliveryTime')}:</span>
                        <span className="store-config-badge">{empresaConfig.tempoEntregaMin} a {empresaConfig.tempoEntregaMax} min</span>
                    </div>
                    <div className="store-config-row">
                        <span className="store-config-label">⚡ {t('orders:config.autoAccept')}:</span>
                        <span className={`store-config-badge store-config-badge--${autoAcceptStatus}`}>{autoAcceptLabel}</span>
                    </div>
                </div>
                <button className="store-config-edit" onClick={handleEditTempoEntrega} type="button">
                    {t('orders:config.edit')}
                </button>
            </div>
        );
    };

    return (
        <>
            <PermissionGate permissions={['default']}>
                <Teste sidebar={sidebar}>
                    {/* Modais */}
                    <ModalEditTempoEntrega
                        setEditTempoEntregaEmpresa={setEditTempoEntregaEmpresa}
                        showEditTempoEntregaEmpresa={showEditTempoEntregaEmpresa}
                        setRefresh={handleRefreshAfterConfigEdit}
                        _idEmpresaEdit={idEmpresa}
                        tempoBalcaoMinBD={empresaConfig.tempoBalcaoMin}
                        tempoBalcaoMaxBD={empresaConfig.tempoBalcaoMax}
                        tempoEntregaMinBD={empresaConfig.tempoEntregaMin}
                        tempoEntregaMaxBD={empresaConfig.tempoEntregaMax}
                        tipoImpressao={empresaConfig.tipoImpressao}
                    />

                    <ModalPedido 
                        setShowPedido={setShowPedido} 
                        showPedido={showPedido}
                        setRefresh={handleRefreshKeepFilter}
                        infoPedido={infoPedido}
                        setInfoPedido={setInfoPedido}
                        handleAvancar={handleAvancar}
                        userID={userID}
                        hasCancelPassword={empresaConfig.hasCancelPassword}
                    />

                    <ModalAddEntregador 
                        showModalAddEntregador={showModalAddEntregador}
                        setShowModalAddEntregador={setShowModalAddEntregador}
                        pedidoToAddEntregador={pedidoToAddEntregador}
                        objIdEmpresa={objIdEmpresa}
                        userID={userID}
                        setRefresh={loadPedidos}
                    />

                    <div className="pedeja-page-container">
                        <div className="pedeja-page-content">
                            <div className="page-title-with-icon">
                                <div className="page-icon"><IoReceiptOutline /></div>
                                <h1 className="page-title">{t('orders:title')}</h1>
                            </div>

                            <div>
                                <div className="mobile-kanban-search-button-wrapper">
                                    <FilterButtons
                                        filtroSelecionado={filtroSelecionado}
                                        onFilterAll={handleFilterAll}
                                        onFilterDelivery={handleFilterDelivery}
                                        onFilterBalcao={handleFilterBalcao}
                                        onFilterMesas={handleFilterMesas}
                                        isMobile={isMobile}
                                    />

                                    <div className="search-container-modern" style={{ width: '100%', maxWidth: '100%' }}>
                                        <div className="search-icon-modern">
                                            <FaSearch />
                                        </div>
                                        <input
                                            type="text"
                                            className="search-input-modern"
                                            placeholder={t('orders:search.placeholder')}
                                            value={searchQuery}
                                            onChange={(e) => {
                                                setSearchQuery(e.target.value);
                                                if (buscarPedidos) {
                                                    buscarPedidos(e.target.value);
                                                }
                                            }}
                                        />
                                    </div>

                                    <div className="div-buttons mobile-new-order">
                                        <div className="continue-button">
                                            <button onClick={handleCadastroPDV} className="new-order-btn">
                                                <AiIcons.AiOutlinePlusCircle className="new-order-icon" />
                                                <span>{t('orders:newOrder')}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {window.innerWidth > 1200 ? (
                            <KanbanGrid>
                                {filterIsLoading || isLoading ? (
                                    <Loading
                                        type='spin'
                                        color='rgb(49, 140, 213)'
                                        height={'auto'}
                                        width={'auto'}
                                        className="loadingSpinHomeFilters"
                                        /* O texto do overlay e desenhado pelo ::after do CSS,
                                           que nao passa por i18n -- por isso vem daqui. */
                                        data-loading-text={t('loadingOrders')}
                                    />
                                ) : null}

                                {/* Pedidos em Análise */}
                                <Ordercolumn>
                                    <KanbanColumn
                                        title={t('orders:columns.analysis')}
                                        count={pedidosOrganizados.analise.length}
                                        pedidos={pedidosOrganizados.analise}
                                        emptyMessage={t('orders:emptyStates.analysis')}
                                        headerContent={<AnaliseHeaderContent />}
                                        containerClass="containerAnalise"
                                        headerClass="header-column-analise"
                                        onShowPedido={handleShowPedido}
                                        onAddEntregador={handleAddEntregador}
                                        onAvancar={handleAvancar}
                                        isUpdating={isUpdating}
                                        pedidosProcessando={pedidosProcessando}
                                        animationDelay="0ms"
                                    />
                                </Ordercolumn>

                                {/* Pedidos em Produção */}
                                <Ordercolumn>
                                    <KanbanColumn
                                        title={t('orders:columns.production')}
                                        count={pedidosOrganizados.producao.length}
                                        pedidos={pedidosOrganizados.producao}
                                        emptyMessage={t('orders:emptyStates.production')}
                                        containerClass="containerProducao"
                                        headerClass="header-column-producao"
                                        onShowPedido={handleShowPedido}
                                        onAddEntregador={handleAddEntregador}
                                        onAvancar={handleAvancar}
                                        isUpdating={isUpdating}
                                        pedidosProcessando={pedidosProcessando}
                                        animationDelay="100ms"
                                    />
                                </Ordercolumn>

                                {/* Pedidos Prontos */}
                                <Ordercolumn>
                                    <KanbanColumn
                                        title={t('orders:columns.ready')}
                                        count={pedidosOrganizados.pronto.length}
                                        pedidos={pedidosOrganizados.pronto}
                                        emptyMessage={t('orders:emptyStates.ready')}
                                        containerClass="containerPronto"
                                        headerClass="header-column-pronto"
                                        onShowPedido={handleShowPedido}
                                        onAddEntregador={handleAddEntregador}
                                        onAvancar={handleAvancar}
                                        isUpdating={isUpdating}
                                        pedidosProcessando={pedidosProcessando}
                                        animationDelay="200ms"
                                    />
                                </Ordercolumn>

                                {/* Pedidos Finalizados */}
                                <Ordercolumn>
                                    <KanbanColumn
                                        title={t('orders:columns.completed')}
                                        count={pedidosOrganizados.finalizado.length}
                                        pedidos={pedidosOrganizados.finalizado}
                                        emptyMessage={t('orders:emptyStates.completed')}
                                        containerClass="containerFinalizado"
                                        headerClass="header-column-finalizado"
                                        onShowPedido={handleShowPedido}
                                        onAddEntregador={handleAddEntregador}
                                        onAvancar={handleAvancar}
                                        showAvancarButton={false}
                                        isUpdating={isUpdating}
                                        pedidosProcessando={pedidosProcessando}
                                        animationDelay="300ms"
                                    />
                                </Ordercolumn>
                            </KanbanGrid>
                        ) : (
                            <MobileKanban
                                tempoBalcaoMinBD={empresaConfig.tempoBalcaoMin}
                                tempoBalcaoMaxBD={empresaConfig.tempoBalcaoMax}
                                tempoEntregaMinBD={empresaConfig.tempoEntregaMin}
                                tempoEntregaMaxBD={empresaConfig.tempoEntregaMax}
                                tipoImpressao={empresaConfig.tipoImpressao}
                                pedidosAnalise={pedidosOrganizados.analise}
                                pedidosFinalizados={pedidosOrganizados.finalizado}
                                pedidosSimples={[...pedidosOrganizados.analise, ...pedidosOrganizados.producao, ...pedidosOrganizados.pronto]}
                                pedidosProducao={pedidosOrganizados.producao}
                                pedidosEntrega={pedidosOrganizados.pronto}
                                pedidosFinalizado={pedidosOrganizados.finalizado}
                                filterIsLoading={filterIsLoading || isLoading}
                                handleShowPedido={handleShowPedido}
                                handleEditTempoEntrega={handleEditTempoEntrega}
                                handleAddEntregador={handleAddEntregador}
                                handleAvancar={handleAvancar}
                                userID={userID}
                                pedidosProcessando={pedidosProcessando}
                            />
                        )}
                    </div>
                </Teste>
            </PermissionGate>
        </>
    );
};

export default User;
